import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, User, Calendar, ArrowRight } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { blogPosts } from '../data/blogPosts';

const BlogPostPage = () => {
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.slug === slug);

  if (!post) {
    return (
      <>
        <Helmet>
          <title>Post Not Found — Searchefy Blog</title>
        </Helmet>
        <div className="min-h-screen bg-[#F9FAFB]">
          <Navbar />
          <main className="pt-32 pb-24">
            <div className="max-w-3xl mx-auto px-6 text-center">
              <h1 className="font-display text-4xl font-bold text-gray-900 mb-4">
                Post Not Found
              </h1>
              <p className="text-gray-600 mb-8">
                The blog post you are looking for does not exist.
              </p>
              <Link to="/blog" className="btn-primary inline-flex items-center gap-2">
                <ArrowLeft size={18} />
                Back to Blog
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const otherPosts = blogPosts.filter((p) => p.slug !== slug).slice(0, 2);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.excerpt,
    author: {
      '@type': 'Person',
      name: post.author,
      url: 'https://pratishjain.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Searchefy',
      logo: {
        '@type': 'ImageObject',
        url: 'https://searchefy.com/logo.png',
      },
    },
    datePublished: post.date,
    dateModified: post.date,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://searchefy.com/blog/${post.slug}`,
    },
  };

  return (
    <>
      <Helmet>
        <title>{`${post.title} — Searchefy Blog`}</title>
        <meta name="description" content={post.excerpt || ''} />
        <link rel="canonical" href={`https://searchefy.com/blog/${post.slug}`} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.title || ''} />
        <meta property="og:description" content={post.excerpt || ''} />
        <meta property="article:author" content={post.author || ''} />
        <meta property="article:published_time" content={post.date || ''} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>

      <div data-testid="blog-post-page" className="min-h-screen bg-[#F9FAFB]">
        <Navbar />

        <main className="pt-24">
          {/* Header */}
          <section className="section-dark py-16 md:py-24">
            <div className="max-w-4xl mx-auto px-6 md:px-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <Link
                  to="/blog"
                  className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-8 transition-colors"
                >
                  <ArrowLeft size={18} />
                  Back to Blog
                </Link>

                <span className="inline-block px-3 py-1 bg-gradient-to-r from-blue-500 to-purple-500 text-white text-sm font-medium rounded-full mb-4">
                  {post.category}
                </span>

                <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-6">
                  {post.title}
                </h1>

                <div className="flex flex-wrap items-center gap-6 text-white/60">
                  <span className="flex items-center gap-2">
                    <User size={18} />
                    {post.author}
                  </span>
                  <span className="flex items-center gap-2">
                    <Calendar size={18} />
                    {new Date(post.date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </span>
                  <span className="flex items-center gap-2">
                    <Clock size={18} />
                    {post.readTime}
                  </span>
                </div>
              </motion.div>
            </div>
          </section>

          {/* Content */}
          <section className="py-16 md:py-24">
            <div className="max-w-3xl mx-auto px-6 md:px-12">
              <motion.article
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="prose prose-lg prose-gray max-w-none
                  prose-headings:font-display prose-headings:tracking-tight prose-headings:text-gray-900
                  prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4
                  prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
                  prose-p:text-gray-600 prose-p:leading-relaxed
                  prose-strong:text-gray-900 prose-strong:font-semibold
                  prose-ul:text-gray-600 prose-ol:text-gray-600
                  prose-li:marker:text-blue-500
                  prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline
                  prose-blockquote:border-l-blue-500 prose-blockquote:bg-blue-50 prose-blockquote:py-1 prose-blockquote:px-4 prose-blockquote:rounded-r-lg
                  prose-code:bg-gray-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-code:before:content-none prose-code:after:content-none"
                dangerouslySetInnerHTML={{ __html: formatContent(post.content) }}
              />

              {/* Author Bio */}
              <div className="mt-16 p-8 bg-white rounded-2xl border border-gray-200">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white text-xl font-bold">
                    PJ
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Written by {post.author}</p>
                    <p className="text-sm text-gray-600">
                      Content Writer & SEO Strategist. Building Searchefy to make link building accessible.
                    </p>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div className="mt-12 p-8 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl text-center">
                <h3 className="font-display text-xl font-bold text-gray-900 mb-2">
                  Ready to put this into practice?
                </h3>
                <p className="text-gray-600 mb-6">
                  Get access to 500+ curated backlink sources and track all your campaigns in one place.
                </p>
                <a
                  href="https://checkout.dodopayments.com/buy/pdt_0Nc1Sd7RjzAPh38CNHxE9?quantity=1"
                  data-testid="blog-post-cta"
                  className="btn-primary inline-flex items-center gap-2"
                >
                  Get Lifetime Access — $9
                  <ArrowRight size={18} />
                </a>
              </div>
            </div>
          </section>

          {/* Related Posts */}
          {otherPosts.length > 0 && (
            <section className="py-16 border-t border-gray-200">
              <div className="max-w-7xl mx-auto px-6 md:px-12">
                <h2 className="font-display text-2xl font-bold text-gray-900 mb-8">
                  Continue Reading
                </h2>
                <div className="grid md:grid-cols-2 gap-8">
                  {otherPosts.map((relatedPost) => (
                    <Link
                      key={relatedPost.slug}
                      to={`/blog/${relatedPost.slug}`}
                      className="group block"
                    >
                      <div className="blog-card">
                        <div className="aspect-video bg-gradient-to-br from-blue-100 to-purple-100 relative flex items-center justify-center">
                          <span className="text-5xl opacity-50">{relatedPost.emoji}</span>
                        </div>
                        <div className="p-6">
                          <span className="text-sm text-blue-600 font-medium">
                            {relatedPost.category}
                          </span>
                          <h3 className="font-display text-lg font-bold text-gray-900 mt-2 group-hover:text-blue-600 transition-colors">
                            {relatedPost.title}
                          </h3>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
};

// Simple markdown-like formatting
function formatContent(content) {
  if (!content) return '';
  
  const lines = content.trim().split('\n');
  let html = '';
  let inList = false;
  
  for (const line of lines) {
    const trimmed = line.trim();
    
    if (!trimmed) {
      if (inList) {
        html += '</ul>';
        inList = false;
      }
      continue;
    }
    
    if (trimmed.startsWith('## ')) {
      if (inList) { html += '</ul>'; inList = false; }
      html += `<h2>${trimmed.slice(3)}</h2>`;
    } else if (trimmed.startsWith('### ')) {
      if (inList) { html += '</ul>'; inList = false; }
      html += `<h3>${trimmed.slice(4)}</h3>`;
    } else if (trimmed.startsWith('- ')) {
      if (!inList) { html += '<ul>'; inList = true; }
      html += `<li>${formatInline(trimmed.slice(2))}</li>`;
    } else {
      if (inList) { html += '</ul>'; inList = false; }
      html += `<p>${formatInline(trimmed)}</p>`;
    }
  }
  
  if (inList) html += '</ul>';
  return html;
}

function formatInline(text) {
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>');
}

export default BlogPostPage;
